import { queueNotification } from '~/shared/notifications/actions'
import { ChangeEvent, Dispatch, FC, SetStateAction, useState } from 'react'
import { useDispatch } from 'react-redux'
import { ProvidersCasesPartialUpdateResponse } from '@fireflyhealth/api'
import { TextField } from '@mui/material'
import { useUpdateCase } from '~/entities/case'
import { useUnsavedChanges } from '~/shared/hooks/useUnsavedChanges'

const InlineEditableField: FC<{
  defaultValue?: string
  selectedCase: number
  valueToBeUpdated: 'description' | 'notes'
  placeholder: string
  isClosed: boolean
  focus?: Dispatch<SetStateAction<HTMLDivElement | null>>
  showError?: boolean
  setEditingCase: (value: boolean) => void
}> = props => {
  const {
    defaultValue,
    selectedCase,
    valueToBeUpdated,
    placeholder,
    isClosed,
    focus,
    showError,
    setEditingCase,
  } = props

  const { mutate: updateCase } = useUpdateCase()
  const dispatch = useDispatch()

  const [descriptionError, setDescriptionError] = useState<boolean>(false)

  const [despValue, setDespValue] = useState<string>(defaultValue || '')

  const { trackUnsavedChange, clearUnsavedChange } = useUnsavedChanges()

  // The text resets to the saved value, and a cleared error flag clears the error, when either changes.
  const [syncedProps, setSyncedProps] = useState({ showError, defaultValue })
  if (showError !== syncedProps.showError || defaultValue !== syncedProps.defaultValue) {
    setSyncedProps({ showError, defaultValue })
    if (showError == false) setDescriptionError(false)
    setDespValue(defaultValue || '')
  }

  const handleOnAfterEditCase = (pc: ProvidersCasesPartialUpdateResponse) => {
    if (!pc) {
      dispatch(
        queueNotification({
          variant: 'error',
          message: 'Case Updation Failed',
        }),
      )
    }
  }

  const updatePatientCase = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.target.value == '' && valueToBeUpdated == 'description') {
      setDescriptionError(true)
    }
    if (e.target.value !== '' && defaultValue !== e.target.value) {
      setEditingCase(true)
      setDescriptionError(false)
      updateCase(
        { id: selectedCase, body: { [valueToBeUpdated]: e.target.value } },
        {
          onSuccess: updatedCase => {
            clearUnsavedChange()
            handleOnAfterEditCase(updatedCase)
          },
          onSettled: () => setEditingCase(false),
        },
      )
    }
  }

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setDespValue(event.target.value)
    trackUnsavedChange()
  }

  return (
    <>
      <TextField
        hiddenLabel
        inputRef={focus}
        disabled={isClosed}
        placeholder={placeholder}
        value={despValue}
        error={descriptionError}
        helperText={descriptionError && 'Description required'}
        multiline
        onChange={handleChange}
        onClick={e => {
          e.stopPropagation()
        }}
        onBlur={updatePatientCase}
        sx={{
          marginLeft: '1.6rem',
          wordBreak: 'break-word',
          fontSize: '1.4rem',
          width: 'auto',
          '& .MuiFormHelperText-root.Mui-error': {
            marginLeft: 0,
          },
          '& .MuiInputBase-root.MuiOutlinedInput-root': {
            padding: 0,
          },
          '& textarea': {
            padding: 0,
            paddingLeft: 1,
            paddingTop: 0.2,
            paddingBottom: 0.2,
            fontSize: '1.4rem',
            color: 'grey.500',
          },
          '& fieldset': {
            border: descriptionError ? '1px solid red' : 'none',
          },
          '& textarea:focus': {
            border: '1px solid black',
            borderColor: 'grey.500',
            borderRadius: 1,
          },
        }}
        slotProps={{
          input: {
            sx: {
              '& .MuiInputBase-input::placeholder': { color: 'common.black', fontWeight: 500 },
            },
          },
        }}
      />
    </>
  )
}

export default InlineEditableField
